import { useEffect, useState } from 'react';
import { VoicemailStatus } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { useTranslation } from '@frontend/i18n';
import { RecentCallRecord, recentCallsTrackingId } from '@frontend/phone-panels';
import { WithTooltip } from '@frontend/phone-ui';
import { useQueueStore } from '@frontend/queue';
import { useThrottledFn } from '@frontend/timer';
import { pendo } from '@frontend/tracking';
import { Button } from '@frontend/design-system';
import { useVoicemailDetailsShallowStore } from '../../stores/voicemail-details-store';
import { VoicemailTableDataRow } from '../all-calls/types';

export const openVoicemailDetails = (channelId: string, locationId: string) => {
  const { enqueue } = useQueueStore.getState();
  enqueue('voicemail-details', {
    payload: {
      type: 'query-then-open-detached',
      voicemailId: channelId,
      locationId: locationId,
    },
  });
};

export const openVoicemailDetailsWithData = (data: VoicemailTableDataRow) => {
  const { enqueue } = useQueueStore.getState();
  enqueue('voicemail-details', {
    payload: {
      type: 'open-detached',
      data,
    },
  });
};

export const VoicemailButton = ({
  callRecord,
  size = 'large',
  stopPropagation = false,
}: {
  callRecord: RecentCallRecord;
  size?: 'large' | 'small';
  stopPropagation?: boolean;
}) => {
  const { t } = useTranslation('calls');
  const { voicemailData } = useVoicemailDetailsShallowStore('voicemailData');
  const isDeleted = callRecord.voicemailStatus === VoicemailStatus.VOICEMAIL_STATUS_DELETE;

  // Persists read/unread status so the button stays correct even after the panel closes
  // (which clears voicemailData from the store). Updated whenever the store has fresh data.
  const [isUnread, setIsUnread] = useState(callRecord.voicemailStatus === VoicemailStatus.VOICEMAIL_STATUS_UNREAD);

  useEffect(() => {
    if (voicemailData && voicemailData.voicemailId === callRecord.channelId) {
      setIsUnread(!voicemailData.readAt);
    }
  }, [voicemailData, callRecord.channelId]);

  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (!callRecord.channelId || !callRecord.locationId) return;
    openVoicemailDetails(callRecord.channelId, callRecord.locationId);
  };

  const handleHover = useThrottledFn(() => {
    pendo.track(recentCallsTrackingId({ context: 'voicemail-message-hover' }), {
      voicemailStatus: callRecord.voicemailStatus,
    });
  }, 500);

  if (isDeleted) {
    return (
      <WithTooltip tooltipText={t('Voicemail deleted')}>
        <Button
          variant='secondary'
          size={size}
          iconName='voicemail-deleted'
          onMouseEnter={handleHover}
          css={{ width: 24, height: 24, minWidth: 24, cursor: 'default' }}
        />
      </WithTooltip>
    );
  }

  return (
    <WithTooltip tooltipText={isUnread ? t('Unread voicemail') : t('Voicemail read')}>
      <Button
        variant='secondary'
        size={size}
        iconName={isUnread ? 'voicemail-unread' : 'voicemail-tiny'}
        onClick={handleClick}
        data-trackingid={recentCallsTrackingId({ context: 'voicemail-message-click' })}
        onMouseEnter={handleHover}
        css={{ width: 24, height: 24, minWidth: 24 }}
      />
    </WithTooltip>
  );
};
